import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useCreateQuestionBankSubjectMutation } from "../../slices/questionBankSubjectApiSlice";

const QuestionBankSubjectCreateScreen = () => {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Quantitative Aptitude");

  const [createQuestionBankSubject, { isLoading }] =
    useCreateQuestionBankSubjectMutation();

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Subject name is required");
      return;
    }

    try {
      await createQuestionBankSubject({
        name: name.trim(),
        description: description.trim(),
        category,
      }).unwrap();

      toast.success("Question bank subject created successfully");

      navigate("/question-bank-subjects");
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.error ||
          "Failed to create question bank subject",
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/question-bank-subjects"
          className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-xl bg-white shadow hover:bg-blue-50 text-blue-600 transition"
        >
          ← Back to Question Bank Subjects
        </Link>

        <div className="bg-white/80 backdrop-blur-lg shadow-2xl rounded-3xl border border-white/30 p-6 md:p-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              Create Question Bank Subject
            </h1>

            <p className="mt-2 text-gray-500">
              Add a new subject for the Question Bank
            </p>
          </div>

          {isLoading && (
            <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
              Creating question bank subject...
            </div>
          )}

          <form onSubmit={submitHandler} className="space-y-6">
            {/* Subject Name */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">
                Subject Name <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Arithmetic"
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 outline-none transition"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">
                Category
              </label>

              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 outline-none transition"
              >
                <option value="Quantitative Aptitude">
                  Quantitative Aptitude
                </option>
                <option value="Reasoning">Reasoning</option>
                <option value="General Awareness">General Awareness</option>
                <option value="English">English</option>
                <option value="Computer Knowledge">Computer Knowledge</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block mb-2 text-sm font-semibold text-gray-700">
                Description
              </label>

              <textarea
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter subject description (optional)..."
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 resize-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 outline-none transition"
              />
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3.5 text-lg font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-60"
              >
                {isLoading ? "Creating..." : "Create Question Bank Subject"}
              </button>

              <Link
                to="/question-bank-subjects"
                className="flex-1 rounded-xl border border-gray-300 bg-white py-3.5 text-center text-lg font-semibold text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuestionBankSubjectCreateScreen;
